import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Button as OriginalButton, Colors } from 'advenio-components'

const Container = styled.div`
  width: 100%;
  background-color: ${Colors.gray300};
`
const Table = styled.table`
  width: 100%;
  border-spacing: 0;
  border-collapse: collapse;
`
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  height: 100%;
  padding: 4px 16px 16px;
  border-spacing: 0;
  border-collapse: collapse;
`
const ConceptText = styled.p`
  margin: 0;
  font-weight: bold;
`
const ConceptMetaInfo = styled.p`
  margin: 0;
  margin-top: 4px;
  color: ${Colors.gray800};
`
const TableRow = styled.tr`
  width: 100%;
`
const TableBodyRow = TableRow.extend`
  &:first-child {
    td {
      padding-top: 16px;
    }
  }
`
const TableCellStyles = `
  padding: 8px 16px;
  font-size: 12px;
`
const TableHeader = styled.th`
  ${TableCellStyles}
  text-align: ${props => props.right ? 'right' : 'left'};
  background-color: ${Colors.gray400};
  text-transform: uppercase;
`
const TableCell = styled.td`
  ${TableCellStyles}
  text-align: ${props => props.right ? 'right' : 'left'};
  font-weight: ${props => props.bold ? 'bold' : ''};
  vertical-align: top;
`
const TotalsSummaryTableElem = styled.table`
  float: right;
  text-align: right;
`
const TotalsSummaryContainerRow = styled.tr``
const TotalsSummaryContainerCell = styled.td`
  padding: 16px;
`
const TotalsSummaryRow = styled.tr`
  width: 100%;
`
const TotalsSummaryCell = styled.td`
  padding: 0 8px;
  font-size: ${props => props.primary ? '16px' : '14px'};
  color: ${props => !props.primary ? Colors.gray700 : ''};
  text-align: ${props => props.alignRight ? 'right' : ''};

  &:last-child {
    padding-right: 0;
  }
`
const TotalsSummaryCellLabel = TotalsSummaryCell.extend`
  font-weight: ${props => props.primary ? 'bold' : ''};
`
const TotalsSummaryCellValue = TotalsSummaryCell.extend`
  text-align: right;
  font-weight: bold;
`

const Button = props => <OriginalButton small marginRight="0" {...props} />

const TotalsSummary = ({ label, value, ...props }) => (
  <TotalsSummaryRow>
    <TotalsSummaryCellLabel {...props}>{label}</TotalsSummaryCellLabel>
    <TotalsSummaryCellValue {...props}>{value}</TotalsSummaryCellValue>
  </TotalsSummaryRow>
)

TotalsSummary.propTypes = {
  /** Label to be displayed */
  label: PropTypes.string.isRequired,
  /** Value to be displayed */
  value: PropTypes.string.isRequired,
}

const TotalsSummaryTable = ({
  retention_total_formatted,
  subtotal_formatted,
  tax_formatted,
  total_formatted,
  total_without_retention_formatted,
}) => (
  <TotalsSummaryTableElem>
    <tbody>
      <TotalsSummary
        label="Total antes de Retenciones"
        value={total_without_retention_formatted}
      />
      <TotalsSummary
        label="Retenciones"
        value={retention_total_formatted}
      />
      <TotalsSummary
        label="Subtotal"
        value={subtotal_formatted}
      />
      <TotalsSummary
        label="IVA"
        value={tax_formatted}
      />
      <TotalsSummary
        primary
        label="Total"
        value={total_formatted}
      />
    </tbody>
  </TotalsSummaryTableElem>
)

TotalsSummaryTable.propTypes = {
  subtotal_formatted: PropTypes.string.isRequired,
  retention_total_formatted: PropTypes.string.isRequired,
  tax_formatted: PropTypes.string.isRequired,
  total_formatted: PropTypes.string.isRequired,
  total_without_retention_formatted: PropTypes.string.isRequired,
}

const StudentReceiptDetail = ({ row, row: { original: receipt }, ...props }) => (
  <Container>
    <Table>
      <thead>
        <TableRow>
          <TableHeader width="15%">Fecha</TableHeader>
          <TableHeader>Concepto</TableHeader>
          <TableHeader right width="20%">Cargo</TableHeader>
          <TableHeader right width="20%">Pago</TableHeader>
        </TableRow>
      </thead>
      <tbody>
        {receipt.transactions.map(transaction => (
          <TableBodyRow key={transaction.id}>
            <TableCell>
              {moment(transaction.registered_at).format('YYYY-MM-DD HH:mm')}
            </TableCell>
            <TableCell>
              <ConceptText>{transaction.description} {transaction.is_retention && '(R)'}{transaction.quantity > 1 && `(${transaction.quantity})`}</ConceptText>
              {transaction.totals &&
                transaction.totals.company &&
                transaction.totals.company.total > 0 && (
                <div>
                  <ConceptMetaInfo>
                    – Costo total del cargo: {transaction.totals.total_formatted}
                  </ConceptMetaInfo>
                  <ConceptMetaInfo>
                    – {transaction.support_as_pct ?
                      `${transaction.totals.company.total}% Apoyo Empresa` :
                      `Apoyo Empresa: ${transaction.totals.company.total_formatted}`
                    }
                  </ConceptMetaInfo>
                </div>
              )}
              {transaction.totals &&
                transaction.totals.parent &&
                transaction.totals.parent.discount && (
                <div>
                  <ConceptMetaInfo>
                    {` ${transaction.totals.parent.discount.description} - ${transaction.totals.parent.discount.amount_formatted}`}
                  </ConceptMetaInfo>
                </div>
              )}
            </TableCell>
            <TableCell right bold>
              {(transaction.type === 'Charge' && transaction.totals.parent.discount) && transaction.totals.parent.discount.total_with_discount_formatted}
              {(transaction.type === 'Charge' && !transaction.totals.parent.discount) && transaction.totals.parent.total_formatted}
            </TableCell>
            <TableCell right bold>
              {transaction.type === 'Payment' && transaction.total_formatted}
            </TableCell>
          </TableBodyRow>
        ))}
        <TotalsSummaryContainerRow>
          <TotalsSummaryContainerCell colSpan="4">
            <TotalsSummaryTable {...receipt} />
          </TotalsSummaryContainerCell>
        </TotalsSummaryContainerRow>
      </tbody>
    </Table>
    <Footer>
      {props.adjust && (
        <Button
          ghost
          onClick={() => props.adjust(receipt)}
        >
          Ajustar transacciones
        </Button>
      )}
      {props.validateReceipt && (
        <Button
          primary
          onClick={() => props.validateReceipt(row.index, receipt.id)}
        >
          Validar
        </Button>
      )}
    </Footer>
  </Container>
)

StudentReceiptDetail.propTypes = {
  /** The component props */
  row: PropTypes.object.isRequired,
  /** Callback function to navigate to the student's profile */
  adjust: PropTypes.func,
  /** Callback function to validate the current student receipt */
  validateReceipt: PropTypes.func,
}

StudentReceiptDetail.defaultProps = {
  adjust: null,
  validateReceipt: null,
}

export default StudentReceiptDetail



// WEBPACK FOOTER //
// ./src/Containers/ReceiptValidations/StudentReceiptDetail.jsx